import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Form, Input, InputNumber, Button, Row, Col,
  Space, message, Typography, Spin, Tag, Tooltip, Divider, ConfigProvider,
} from 'antd';
import {
  PlusOutlined, MinusCircleOutlined, SaveOutlined, DownloadOutlined,
  InfoCircleOutlined, MedicineBoxOutlined, AppstoreOutlined, FileSearchOutlined,
} from '@ant-design/icons';
import { protocolAPI } from '../services/api';
import { gray } from '../theme/tokens';

const { TextArea } = Input;

const oneLineLabel = (text: string) => (
  <span style={{ whiteSpace: 'nowrap' }}>{text}</span>
);

const GRID_HEADER_BG = '#f8fafc';
const GRID_BORDER = '#e2e8f0';

const gridShellStyle: React.CSSProperties = {
  overflowX: 'auto',
  border: `1px solid ${GRID_BORDER}`,
  borderRadius: 10,
  background: '#fff',
};

const gridHeaderStyle = (template: string, accent: string): React.CSSProperties => ({
  display: 'grid',
  gridTemplateColumns: template,
  gap: 8,
  minWidth: 1120,
  padding: '10px 12px',
  borderBottom: `1px solid ${GRID_BORDER}`,
  background: GRID_HEADER_BG,
  color: accent,
  fontSize: 12,
  fontWeight: 700,
});

const gridRowStyle = (template: string, highlighted = false): React.CSSProperties => ({
  display: 'grid',
  gridTemplateColumns: template,
  gap: 8,
  minWidth: 1120,
  padding: '10px 12px',
  alignItems: 'start',
  background: highlighted ? '#fcfcfd' : '#fff',
  borderBottom: `1px solid ${gray[100]}`,
});

const gridCellItemStyle = { marginBottom: 0 };

const rowBadgeStyle: React.CSSProperties = {
  minHeight: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 7,
  background: '#eef2ff',
  color: '#4f46e5',
  fontSize: 11,
  fontWeight: 700,
};

const actionCellStyle: React.CSSProperties = {
  minHeight: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
};

const iconOnlyActionStyle: React.CSSProperties = {
  height: 24,
  width: 24,
  minWidth: 24,
  padding: 0,
};

/* ── 서브섹션 구분선 ── */
const Sub: React.FC<{ title: string }> = ({ title }) => (
  <Divider orientation={'left' as any} orientationMargin={0} plain
    style={{ fontSize: 11, color: gray[400], borderColor: '#e8edf2', margin: '2px 0 8px' }}>
    {title}
  </Divider>
);

const Section: React.FC<{ icon: React.ReactNode; title: string; color: string; children: React.ReactNode }> = ({
  icon, title, color, children,
}) => (
  <section style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
    <div style={{
      display: 'flex', alignItems: 'center', gap: 7, minHeight: 34,
      padding: '7px 12px', borderBottom: '1px solid #eef2f7', background: '#f8fafc',
      color, fontSize: 13, fontWeight: 700,
    }}>
      {icon}
      <span>{title}</span>
    </div>
    <div style={{ padding: '10px 12px 2px' }}>
      {children}
    </div>
  </section>
);

/* ── Form.Item 공통 스타일 (marginBottom 축소) ── */
const FI = { marginBottom: 8 };

interface ProtocolFormPageProps {
  editId?: string;
  onClose?: () => void;
}

const ProtocolFormPage: React.FC<ProtocolFormPageProps> = ({ editId, onClose }) => {
  const params = useParams<{ id: string }>();
  const id = editId || params.id;
  const isEdit = Boolean(id);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const isModal = Boolean(onClose);

  const goBack = () => {
    if (isModal) onClose!();
    else navigate('/protocols');
  };

  useEffect(() => {
    if (isEdit && id) {
      setLoading(true);
      protocolAPI.get(Number(id))
        .then(({ data }) => {
          form.setFieldsValue({
            ...data,
            treatments: data.treatments?.length > 0 ? data.treatments : [{}],
            plot_designs: data.plot_designs?.length > 0 ? data.plot_designs : [{}],
          });
        })
        .finally(() => setLoading(false));
    } else {
      form.setFieldsValue({
        treatments: [
          { pesticide_name: '이미녹타딘트리스알베실레이트 액상수화제', active_ingredient_pct: '30', dilution_rate: '500배', treatment_timing: '월동 전, 월동 후 각 1회 수관처리', standard_amount: '500배', double_amount: '250배', test_year_number: 2, client_company: '팜한농' },
          { pesticide_name: '티오파네이트메틸 액상수화제', active_ingredient_pct: '40', dilution_rate: '500', treatment_timing: '〃', standard_amount: '500', double_amount: '250', test_year_number: 2, client_company: '팜한농' },
          { pesticide_name: '이미녹타딘트리아세테이트 액제(대조)', active_ingredient_pct: '25', dilution_rate: '500', treatment_timing: '〃', standard_amount: '-', double_amount: '-' },
          { pesticide_name: '무처리' },
        ],
        plot_designs: [
          { category: '약 효', treatment_count: '4', replication_count: 3, total_plots: 12, trees_per_plot: '3 주', required_trees: '36 주', total_required_trees: '46 주' },
          { category: '약 해', treatment_count: '5×2', replication_count: 3, total_plots: 30, trees_per_plot: '1/3 주', required_trees: '10 주', total_required_trees: '' },
        ],
        protocol_code: 'SR24F-AEVC02',
        title: '사과 부란병 약제방제 효과시험(2024동)',
        institution: '샤프트리서치',
        test_year: '2024~2025년',
        manager: '안창현, 최승환',
        planned_start: '10월 상순',
        planned_end: '6월 하순',
        test_location: '강원 정선군 임계면(노지)',
        purpose: '사과 부란병에 대한 우수 방제약제를 선발하여 농약 품목등록 자료로 활용하고자 함.',
        target_disease: '부란병',
        target_disease_scientific: 'Valsa ceratosperma',
        test_crop: '사과',
        plot_design: '완전임의배치법',
        replications: 3,
        survey_items: '1) 약효: 발병환부수(무처리 최소발병률: 10개)\n   (구당 전주에 대한 발병환부수 조사)\n2) 약해: 외관상 나타나는 약해유무 5회(발아 후 3, 7일 및 개화 후 3, 7, 14일)\n   달관조사',
      });
    }
  }, [id, isEdit, form]);

  const onFinish = async (values: any) => {
    setSaving(true);
    try {
      if (isEdit && id) {
        await protocolAPI.update(Number(id), values);
        message.success('수정되었습니다.');
      } else {
        await protocolAPI.create(values);
        message.success('등록되었습니다.');
      }
      goBack();
    } catch (e: any) {
      message.error(e.response?.data?.detail || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    if (!id) return;
    try {
      const { data } = await protocolAPI.downloadHwpx(Number(id));
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${form.getFieldValue('protocol_code') || 'protocol'}.hwpx`;
      a.click();
      window.URL.revokeObjectURL(url);
      message.success('HWPX 파일이 다운로드되었습니다.');
    } catch {
      message.error('다운로드에 실패했습니다.');
    }
  };

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '60px auto' }} />;

  /* ────────────────────────────────
     Tab 1 : 기본정보
  ──────────────────────────────── */
  const tab1 = (
    <>
      <Row gutter={[12, 0]}>
        <Col xs={24} md={6}>
          <Form.Item name="protocol_code" label="프로토콜 코드" style={FI}>
            <Input placeholder="SR24F-AEVC02" style={{ fontFamily: 'monospace', fontWeight: 600 }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={18}>
          <Form.Item name="title" label="항목명" style={FI} rules={[{ required: true, message: '항목명을 입력하세요' }]}>
            <Input placeholder="사과 부란병 약제방제 효과시험(2024동)" />
          </Form.Item>
        </Col>
      </Row>

      <Sub title="시험 주체" />
      <Row gutter={[12, 0]}>
        <Col xs={24} md={6}>
          <Form.Item name="institution" label="시험기관" style={FI}>
            <Input placeholder="샤프트리서치" />
          </Form.Item>
        </Col>
        <Col xs={24} md={5}>
          <Form.Item name="test_year" label="시험년도" style={FI}>
            <Input placeholder="2024~2025년" />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item name="manager" label="담당자" style={FI}>
            <Input placeholder="안창현, 최승환" />
          </Form.Item>
        </Col>
      </Row>

      <Sub title="일정 및 장소" />
      <Row gutter={[12, 0]}>
        <Col xs={24} md={5}>
          <Form.Item name="planned_start" label="실험예정시기" style={FI}>
            <Input placeholder="10월 상순" />
          </Form.Item>
        </Col>
        <Col xs={24} md={5}>
          <Form.Item name="planned_end" label="실험종료시기" style={FI}>
            <Input placeholder="6월 하순" />
          </Form.Item>
        </Col>
        <Col xs={24} md={14}>
          <Form.Item name="test_location" label="시험장소" style={FI}>
            <Input placeholder="강원 정선군 임계면(노지)" />
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  /* ────────────────────────────────
     Tab 2 : 시험목적·방법
  ──────────────────────────────── */
  const tab2 = (
    <>
      <Sub title="1. 시험목적" />
      <Form.Item name="purpose" style={FI}>
        <TextArea rows={3} placeholder="시험 목적을 입력하세요" />
      </Form.Item>

      <Sub title="가. 대상병해" />
      <Row gutter={[12, 0]}>
        <Col xs={24} md={6}>
          <Form.Item name="target_disease" label="대상병해" style={FI}>
            <Input placeholder="부란병" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item name="target_disease_scientific" label="학명" style={FI}>
            <Input placeholder="Valsa ceratosperma" style={{ fontStyle: 'italic' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item name="test_crop" label="시험작물" style={FI}>
            <Input placeholder="사과" />
          </Form.Item>
        </Col>
      </Row>

      <Sub title="나. 시험작물 품종" />
      <Row gutter={[12, 0]}>
        <Col xs={24} md={6}>
          <Form.Item name="test_variety_efficacy" label="품종(약효)" style={FI}>
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={5}>
          <Form.Item name="test_variety_damage1" label="품종(약해1)" style={FI}>
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={5}>
          <Form.Item name="test_variety_damage2" label="품종(약해2)" style={FI}>
            <Input />
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  /* ────────────────────────────────
     Tab 3 : 처리내용
  ──────────────────────────────── */
  const tab3 = (
    <Form.List name="treatments">
      {(fields, { add, remove }) => (
        <>
          <div style={gridShellStyle}>
            <div style={gridHeaderStyle('56px 2.3fr 0.9fr 1fr 1.6fr 0.9fr 0.9fr 0.8fr 1.1fr 72px', '#b45309')}>
              <div>번호</div>
              <div>시험약제</div>
              <div>주성분(%)</div>
              <div>희석배수</div>
              <div>처리시기 및 방법</div>
              <div>기준량</div>
              <div>배량</div>
              <div>년차</div>
              <div>의뢰회사</div>
              <div />
            </div>
            {fields.map(({ key, name, ...restField }, idx) => (
              <div key={key} style={gridRowStyle('56px 2.3fr 0.9fr 1fr 1.6fr 0.9fr 0.9fr 0.8fr 1.1fr 72px', idx % 2 === 1)}>
                <div style={rowBadgeStyle}>{idx + 1}</div>
                <Form.Item {...restField} name={[name, 'pesticide_name']} style={gridCellItemStyle}>
                  <Input placeholder="약제명" size="small" />
                </Form.Item>
                <Form.Item {...restField} name={[name, 'active_ingredient_pct']} style={gridCellItemStyle}>
                  <Input size="small" placeholder="예: 23(8+15)" />
                </Form.Item>
                <Form.Item {...restField} name={[name, 'dilution_rate']} style={gridCellItemStyle}>
                  <Input size="small" />
                </Form.Item>
                <Form.Item {...restField} name={[name, 'treatment_timing']} style={gridCellItemStyle}>
                  <Input size="small" />
                </Form.Item>
                <Form.Item {...restField} name={[name, 'standard_amount']} style={gridCellItemStyle}>
                  <Input size="small" />
                </Form.Item>
                <Form.Item {...restField} name={[name, 'double_amount']} style={gridCellItemStyle}>
                  <Input size="small" />
                </Form.Item>
                <Form.Item {...restField} name={[name, 'test_year_number']} style={gridCellItemStyle}>
                  <InputNumber style={{ width: '100%' }} size="small" />
                </Form.Item>
                <Form.Item {...restField} name={[name, 'client_company']} style={gridCellItemStyle}>
                  <Input size="small" />
                </Form.Item>
                <div style={actionCellStyle}>
                  {fields.length > 1 && (
                    <Tooltip title="행 삭제">
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<MinusCircleOutlined />}
                        onClick={() => remove(name)}
                        style={iconOnlyActionStyle}
                      />
                    </Tooltip>
                  )}
                </div>
              </div>
            ))}
            <Button
              type="dashed"
              onClick={() => add()}
              block
              icon={<PlusOutlined />}
              size="small"
              style={{ margin: 8, marginTop: 10, borderColor: '#d97706', color: '#d97706', height: 28 }}
            >
            약제 추가
            </Button>
          </div>
        </>
      )}
    </Form.List>
  );

  /* ────────────────────────────────
     Tab 4 : 시험구 배치 & 조사항목
  ──────────────────────────────── */
  const tab4 = (
    <>
      <Sub title="라. 시험구 배치 및 면적" />
      <Row gutter={[12, 0]} style={{ marginBottom: 4 }}>
        <Col xs={24} md={7}>
          <Form.Item name="plot_design" label="배치법" style={FI}>
            <Input placeholder="완전임의배치법" size="small" />
          </Form.Item>
        </Col>
        <Col xs={24} md={5}>
          <Form.Item name="replications" label="반복수" style={FI}>
            <InputNumber style={{ width: '100%' }} size="small" />
          </Form.Item>
        </Col>
      </Row>

      <Form.List name="plot_designs">
        {(fields, { add, remove }) => (
          <>
            <div style={gridShellStyle}>
              <div style={gridHeaderStyle('56px 1fr 0.9fr 0.9fr 0.9fr 1.25fr 1.25fr 1.35fr 72px', '#047857')}>
                <div>번호</div>
                <div>구분</div>
                <div>처리수</div>
                <div>반복수</div>
                <div>총구수</div>
                <div>구당주수</div>
                <div>소요주수</div>
                <div>총소요주수</div>
                <div />
              </div>
              {fields.map(({ key, name, ...restField }, idx) => (
                <div key={key} style={gridRowStyle('56px 1fr 0.9fr 0.9fr 0.9fr 1.25fr 1.25fr 1.35fr 72px', idx % 2 === 1)}>
                  <div style={{ ...rowBadgeStyle, background: '#ecfdf5', color: '#047857' }}>{idx + 1}</div>
                  <Form.Item {...restField} name={[name, 'category']} style={gridCellItemStyle}>
                    <Input size="small" />
                  </Form.Item>
                  <Form.Item {...restField} name={[name, 'treatment_count']} style={gridCellItemStyle}>
                    <Input size="small" />
                  </Form.Item>
                  <Form.Item {...restField} name={[name, 'replication_count']} style={gridCellItemStyle}>
                    <InputNumber style={{ width: '100%' }} size="small" />
                  </Form.Item>
                  <Form.Item {...restField} name={[name, 'total_plots']} style={gridCellItemStyle}>
                    <InputNumber style={{ width: '100%' }} size="small" />
                  </Form.Item>
                  <Form.Item {...restField} name={[name, 'trees_per_plot']} style={gridCellItemStyle}>
                    <Input size="small" />
                  </Form.Item>
                  <Form.Item {...restField} name={[name, 'required_trees']} style={gridCellItemStyle}>
                    <Input size="small" />
                  </Form.Item>
                  <Form.Item {...restField} name={[name, 'total_required_trees']} style={gridCellItemStyle}>
                    <Input size="small" />
                  </Form.Item>
                  <div style={actionCellStyle}>
                    {fields.length > 1 && (
                      <Tooltip title="행 삭제">
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<MinusCircleOutlined />}
                          onClick={() => remove(name)}
                          style={iconOnlyActionStyle}
                        />
                      </Tooltip>
                    )}
                  </div>
                </div>
              ))}
              <Button
                type="dashed"
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
                size="small"
                style={{ margin: 8, marginTop: 10, marginBottom: 12, borderColor: '#059669', color: '#059669', height: 28 }}
              >
              시험구 추가
              </Button>
            </div>
          </>
        )}
      </Form.List>

      <Sub title="마. 주요조사 항목" />
      <Form.Item name="survey_items" style={{ marginBottom: 4 }}>
        <TextArea rows={4} placeholder="약효 및 약해 조사 항목을 입력하세요" size="small" />
      </Form.Item>
    </>
  );

  const formContent = (
    <div style={{ display: 'grid', gap: 10 }}>
      <Section icon={<InfoCircleOutlined />} title="기본정보" color="#4f46e5">
        {tab1}
      </Section>
      <Section icon={<FileSearchOutlined />} title="시험목적·방법" color="#0ea5e9">
        {tab2}
      </Section>
      <Section icon={<MedicineBoxOutlined />} title="처리내용" color="#d97706">
        {tab3}
      </Section>
      <Section icon={<AppstoreOutlined />} title="시험구·조사" color="#059669">
        {tab4}
      </Section>
    </div>
  );

  /* ─── 액션 바 (공통) ─── */
  const actionBar = (
    <div style={{
      display: 'flex', justifyContent: isModal ? 'flex-end' : 'space-between', alignItems: 'center',
      padding: isModal ? '8px 12px' : '10px 12px',
      borderBottom: '1px solid #e8edf2', background: '#fff', flexShrink: 0,
    }}>
      {!isModal && (
        <div>
          <Typography.Title level={4} style={{ margin: 0, color: gray[800] }}>
            {isEdit ? '시험계획서 수정' : '새 시험계획서 작성'}
          </Typography.Title>
        </div>
      )}
      <Space>
        {isEdit && (
          <Button icon={<DownloadOutlined />} onClick={handleDownload} size="small" style={{ borderRadius: 6 }}>
            HWPX 다운로드
          </Button>
        )}
        <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving} size="small" style={{ borderRadius: 6 }}>
          {isEdit ? '수정 저장' : '계획서 등록'}
        </Button>
        <Button onClick={goBack} size="small" style={{ borderRadius: 6 }}>취소</Button>
      </Space>
    </div>
  );

  /* ─── 입력 영역 ─── */
  const editArea = (
    <ConfigProvider theme={{ components: { Form: { itemMarginBottom: 8 } } }}>
      <div style={{
        flex: 1, overflowY: 'auto', padding: '10px 12px 12px',
        background: '#fff',
      }}>
        {formContent}
      </div>
    </ConfigProvider>
  );

  /* ─── 모달 모드: flex column (고정 높이) ─── */
  if (isModal) {
    return (
      <Form form={form} layout="horizontal" labelCol={{ flex: '108px' }} onFinish={onFinish}
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {actionBar}
        {editArea}
      </Form>
    );
  }

  /* ─── 전체화면 모드 ─── */
  return (
    <Form form={form} layout="horizontal" labelCol={{ flex: '108px' }} onFinish={onFinish}>
      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', marginBottom: 12 }}>
        {actionBar}
        <div style={{ padding: 12 }}>
          <ConfigProvider theme={{ components: { Form: { itemMarginBottom: 8 } } }}>
            {formContent}
          </ConfigProvider>
        </div>
      </div>
    </Form>
  );
};

export default ProtocolFormPage;
